import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { apiGetVibeProcess, apiCreateVibeProcess, apiCreateVibeProcessWorkflow, apiUpdateVibeProcess } from "../../../services/WorkflowService";
import { apiGetEmployeeRecord, apiGetCustomPropertiesForWorkspace, apiDeleteCustomProperty, apiGetEmployeeRecordsForWorkspace } from "../../../services/EmployeeRecordService";
import { VibeProcess, EmployeeRecord, CustomProperty, CustomPropertyValue, CreateVibeProcessWorkflowPayload, VibeProcessLayout } from "winslow-core";
// Updated to include workflowPrompt support
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { Button, Icon } from "../../../ds2/components";
import { MySwitch } from "../../shared/MySwitch";
import { MyTooltip } from "../../common/MyTooltip";
import { MentionsInput, Mention } from "react-mentions";
import { selectEmployeeRecords, setEmployeeRecords } from "../../../redux/features/agent/agentSlice";
import { getDefaultUserAvatar } from "winslow-core";
import { ValidatedImage } from "../../common/ValidatedImage";
import { 
  Save, 
  Play, 
  Pause, 
  Settings, 
  Code, 
  FileText, 
  Users, 
  Calendar,
  Edit3,
  Trash2,
  Copy,
  Info,
  // GripVertical,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Shield,
  X,
  AlertCircle,
  Plus
} from "lucide-react";
import "./AppVibeProcessEditor.css";
import "../app-customize/AppCustomProperty.css";
import { AppCustomProperty } from "../app-customize/AppCustomProperty";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import WorkflowDiagram from "./WorkflowDiagram";
import ModalConfirm from "../../policy-studio/components/ModalConfirm";
import { AddPropertyModal } from "./AddPropertyModal";

const MentionsInputAny = MentionsInput as unknown as React.FC<any>;
const MentionAny = Mention as unknown as React.FC<any>;

type AppVibeProcessEditorProps = {
  vpid?: string;
}

export const AppVibeProcessEditor = ({ vpid }: AppVibeProcessEditorProps) => {
  const { vibeProcessId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const storeUser = useSelector(selectUser);
  const uiStates = useSelector(selectUIStates);
  const employeeRecords = useSelector(selectEmployeeRecords);
  
  const [vibeProcess, setVibeProcess] = useState<VibeProcess | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  
  // Form state
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [creationPrompt, setCreationPrompt] = useState<string>("");
  const [workflowPrompt, setWorkflowPrompt] = useState<string>("");
  const [workflowPromptPlainText, setWorkflowPromptPlainText] = useState<string>("");
  const [customProperties, setCustomProperties] = useState<string>("[]");
  const [active, setActive] = useState<boolean>(true);
  const [allowInReporting, setAllowInReporting] = useState<boolean>(true);
  const workflowPromptRef = useRef<HTMLTextAreaElement | null>(null);
  
  // Custom properties state
  const [workspaceCustomProperties, setWorkspaceCustomProperties] = useState<CustomProperty[]>([]);
  const [vibeProcessProperties, setVibeProcessProperties] = useState<CustomProperty[]>([]);
  const [customPropertyValues, setCustomPropertyValues] = useState<Map<string, any>>(new Map());
  
  // Two-column layout state
  const [leftColumnProperties, setLeftColumnProperties] = useState<CustomProperty[]>([]);
  const [rightColumnProperties, setRightColumnProperties] = useState<CustomProperty[]>([]);
  
  // Required properties state (array of property labels)
  const [requiredProperties, setRequiredProperties] = useState<string[]>([]);
  
  // Drag and drop state (commented out)
  // const [draggedProperty, setDraggedProperty] = useState<string | null>(null);
  // const [dropTarget, setDropTarget] = useState<{column: string, index: number} | null>(null);
  
  // Menu state
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  
  // Modal state
  const [showVisualizeModal, setShowVisualizeModal] = useState<boolean>(false);
  const [showSourceModal, setShowSourceModal] = useState<boolean>(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [propertyToDelete, setPropertyToDelete] = useState<{ label: string; display: string; id?: string; isVibe: boolean } | null>(null);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState<boolean>(false);
  
  // Workflow source editing state
  const [workflowSource, setWorkflowSource] = useState<string>("");
  const [isEditingSource, setIsEditingSource] = useState<boolean>(false);
  const [sourceError, setSourceError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(setUIStates({...uiStates, hrisLocation: "Edit Vibe Process"}));
  }, []);
  
  // Load employee records for mentions
  useEffect(() => {
    if (storeUser?.currentWorkspaceId && (!employeeRecords || employeeRecords.length === 0)) {
      const loadEmployeeRecords = async () => {
        try {
          const workspaceId = storeUser.currentWorkspaceId;
          if (!workspaceId) return;
          const records = await apiGetEmployeeRecordsForWorkspace(workspaceId);
          if (records) {
            dispatch(setEmployeeRecords(records));
          }
        } catch (error) {
          console.error("Error loading employee records:", error);
        }
      };
      loadEmployeeRecords();
    }
  }, [storeUser?.currentWorkspaceId, employeeRecords, dispatch]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenu && !(event.target as Element).closest('.app-vibe-process-editor-property-menu')) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenu]);

  useEffect(() => {
    const id = vpid || vibeProcessId;

    const loadData = async () => {
      

      if (id && id !== "new") {
        await loadVibeProcess(id);
      } else {
        setLoading(false);
      }
    };

    loadData();
  }, [vpid, vibeProcessId]);


  // // Load custom properties for workspace
  // useEffect(() => {
  //   if (storeUser?.currentWorkspaceId) {
  //     loadCustomProperties();
  //   }
  // }, [storeUser?.currentWorkspaceId]);

  const loadCustomProperties = async () => {
    if (!storeUser?.currentWorkspaceId) return;

    try {
      console.log("Loading custom properties for workspace:", storeUser.currentWorkspaceId);
      const properties = await apiGetCustomPropertiesForWorkspace(storeUser.currentWorkspaceId);
      console.log("Loaded custom properties:", properties);
      if (properties) {
        setWorkspaceCustomProperties(properties);
        console.log("Set workspace custom properties:", properties.length, "properties");
      }
    } catch (error) {
      console.error("Error loading custom properties:", error);
    }
  };

  const loadVibeProcess = async (processId: string) => {

    if (!storeUser?.currentWorkspaceId) return;

    const workspaceCustomProperties = await apiGetCustomPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");

    console.error("[AppVibeProcessEditor/loadVibeProcess] workspaceCustomProperties: ", workspaceCustomProperties);
    if (!workspaceCustomProperties) {
      toast("Failed to load workspace custom properties.", toastFail);
      return;
    }
    
    setWorkspaceCustomProperties(workspaceCustomProperties);

    const process = await apiGetVibeProcess(processId);
    
    if (process) {
      setVibeProcess(process);
      setName(process.name || "");
      setDescription(process.description || "");
      setCreationPrompt(process.creationPrompt || "");
      const savedWorkflowPrompt = process.workflowPrompt || "";
      setWorkflowPrompt(savedWorkflowPrompt);
      // Extract plain text from mentions markup for character counting
      const plainText = savedWorkflowPrompt.replace(/@\[(.*?)\]\((.*?)\)/g, "@$1");
      setWorkflowPromptPlainText(plainText);
      setCustomProperties(process.customProperties || "[]");
      setActive(process.active);
      setAllowInReporting(process.allowInReporting ?? true);
      
      // Parse required properties from requiredProperties field
      try {
        const requiredProps = JSON.parse(process.requiredProperties || "[]") as string[];
        setRequiredProperties(requiredProps);
      } catch (error) {
        console.error("Error parsing required properties:", error);
        setRequiredProperties([]);
      }
      
      // Parse custom properties from customProperties field (not description)
      parseVibeProcessProperties(process.customProperties, workspaceCustomProperties, process.layout);
      
    }

  };

  const parseVibeProcessProperties = (customPropertiesJson: string, workspaceCustomProperties: CustomProperty[], layoutJson?: string) => {
    
    try {
      console.log("parseVibeProcessProperties called with:", customPropertiesJson);
      console.log("Layout JSON:", layoutJson);
      
      // Parse the custom properties JSON
      const properties = JSON.parse(customPropertiesJson || "[]");
      console.log("Parsed properties:", properties);
      
      console.log("Available workspace properties:", workspaceCustomProperties);
      
      const vibeProcessProps: CustomProperty[] = [];
      
      properties.forEach((prop: any) => {
        console.log("Processing property:", prop.label, "type:", prop.type);
        // Find matching custom property in workspace (treat all properties the same)
        const matchingProp = workspaceCustomProperties.find(cp => cp.label === prop.label);
        if (matchingProp) {
          console.log("Found matching workspace property:", matchingProp.label);
          vibeProcessProps.push(matchingProp);
        } else {
          console.log("No matching workspace property found for:", prop.label);
        }
      });
      
      console.log("Final vibe process properties:", vibeProcessProps.length, "properties");
      setVibeProcessProperties(vibeProcessProps);
      
      // Apply layout if it exists, otherwise use default distribution
      if (layoutJson) {
        try {
          const layout = JSON.parse(layoutJson) as VibeProcessLayout;
          applyLayoutToProperties(vibeProcessProps, layout);
        } catch (layoutError) {
          console.error("Error parsing layout, using default distribution:", layoutError);
          distributePropertiesToColumns(vibeProcessProps);
        }
      } else {
        // No layout exists, create default and save it
        const defaultLayout = createDefaultLayout(vibeProcessProps);
        distributePropertiesToColumns(vibeProcessProps);
        // Note: We'll save the layout when the user saves the vibe process
      }
      
      console.log("Left column properties:", leftColumnProperties.length);
      console.log("Right column properties:", rightColumnProperties.length);
    } catch (error) {
      console.error("Error parsing vibe process properties:", error);
      setVibeProcessProperties([]);
      setLeftColumnProperties([]);
      setRightColumnProperties([]);
    }
  };

  const distributePropertiesToColumns = (properties: CustomProperty[]) => {
    console.log("distributePropertiesToColumns called with:", properties.length, "properties");
    const leftColumn: CustomProperty[] = [];
    const rightColumn: CustomProperty[] = [];
    
    properties.forEach((property, index) => {
      if (index % 2 === 0) {
        leftColumn.push(property);
      } else {
        rightColumn.push(property);
      }
    });
    
    console.log("Distributed to columns - Left:", leftColumn.length, "Right:", rightColumn.length);
    setLeftColumnProperties(leftColumn);
    setRightColumnProperties(rightColumn);
  };

  const applyLayoutToProperties = (properties: CustomProperty[], layout: VibeProcessLayout) => {
    console.log("applyLayoutToProperties called with layout:", layout);
    
    // Create a map of property labels to properties for quick lookup
    const propertyMap = new Map<string, CustomProperty>();
    properties.forEach(prop => {
      propertyMap.set(prop.label, prop);
    });
    
    // Build left column from layout
    const leftColumn: CustomProperty[] = [];
    layout.leftColumn.forEach(label => {
      const prop = propertyMap.get(label);
      if (prop) {
        leftColumn.push(prop);
      }
    });
    
    // Build right column from layout
    const rightColumn: CustomProperty[] = [];
    layout.rightColumn.forEach(label => {
      const prop = propertyMap.get(label);
      if (prop) {
        rightColumn.push(prop);
      }
    });
    
    // Add any properties not in the layout to the left column (in case layout is incomplete)
    properties.forEach(prop => {
      if (!layout.leftColumn.includes(prop.label) && !layout.rightColumn.includes(prop.label)) {
        leftColumn.push(prop);
      }
    });
    
    console.log("Applied layout - Left:", leftColumn.length, "Right:", rightColumn.length);
    setLeftColumnProperties(leftColumn);
    setRightColumnProperties(rightColumn);
  };

  const createDefaultLayout = (properties: CustomProperty[]): VibeProcessLayout => {
    const leftColumn: string[] = [];
    const rightColumn: string[] = [];
    
    properties.forEach((property, index) => {
      if (index % 2 === 0) {
        leftColumn.push(property.label);
      } else {
        rightColumn.push(property.label);
      }
    });
    
    return { leftColumn, rightColumn };
  };

  const getCurrentLayout = (): VibeProcessLayout => {
    return {
      leftColumn: leftColumnProperties.map(p => p.label),
      rightColumn: rightColumnProperties.map(p => p.label)
    };
  };

  const parseCustomPropertyValues = (customPropertiesJson: string) => {
    try {
      const propertyValues = JSON.parse(customPropertiesJson || "[]") as CustomPropertyValue[];
      const valuesMap = new Map<string, any>();
      
      propertyValues.forEach((prop) => {
        valuesMap.set(prop.label, prop.data);
      });
      
      setCustomPropertyValues(valuesMap);
    } catch (error) {
      console.error("Error parsing custom property values:", error);
      setCustomPropertyValues(new Map());
    }
  };

  const handleSave = async () => {
    if (!storeUser?.currentWorkspaceId || !storeUser?.id) {
      return;
    }

    try {
      setSaving(true);
      
      if (vibeProcess) {
        // Update existing process
        const currentLayout = getCurrentLayout();
        const layoutJson = JSON.stringify(currentLayout);
        
        const requiredPropertiesJson = JSON.stringify(requiredProperties);
        
        const updatePayload = {
          name,
          description,
          creationPrompt,
          workflowPrompt,
          customProperties,
          requiredProperties: requiredPropertiesJson,
          layout: layoutJson,
          active,
          allowInReporting
        };
        
        console.log("Updating vibe process:", updatePayload);
        const updatedProcess = await apiUpdateVibeProcess(vibeProcess.id, updatePayload);
        
        if (updatedProcess) {
          setVibeProcess(updatedProcess);
          toast("Vibe process updated successfully!", toastSuccess);
        } else {
          toast("Failed to update vibe process.", toastFail);
        }
      } else {
        // Create new process
        const newProcess = await apiCreateVibeProcess({
          creationPrompt,
          workflowPrompt,
          ownerEmployeeRecordId: storeUser.id, // Assuming user ID maps to employee record
          workspaceId: storeUser.currentWorkspaceId
        });
        
        if (newProcess) {
          setVibeProcess(newProcess);
          navigate(`/app/vibe-coder/${newProcess.id}`);
          toast("Vibe process created successfully!", toastSuccess);
        } else {
          toast("Failed to create vibe process.", toastFail);
        }
      }
      
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving vibe process:", error);
      toast("Error saving vibe process.", toastFail);
    } finally {
      setSaving(false);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setHasChanges(true);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setHasChanges(true);
  };

  const handleCreationPromptChange = (value: string) => {
    setCreationPrompt(value);
    setHasChanges(true);
  };

  const handleWorkflowPromptChange = (value: string, plainText?: string) => {
    setWorkflowPrompt(value);
    if (plainText !== undefined) {
      setWorkflowPromptPlainText(plainText);
    }
    setHasChanges(true);
  };
  
  // Get employee record mentions only
  const getEmployeeRecordMentions = (query: string = "") => {
    if (!employeeRecords || employeeRecords.length === 0) return [];
    
    const lowercaseQuery = query.toLowerCase().trim();
    
    // If query is empty, return all employee records (limit to 8)
    let filtered = employeeRecords;
    if (lowercaseQuery) {
      filtered = employeeRecords.filter((er) => {
        const firstName = erGetCPValue(er, "system-firstName") || "";
        const lastName = erGetCPValue(er, "system-lastName") || "";
        const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
        const email = er.workEmail || "";
        return (
          displayName.toLowerCase().includes(lowercaseQuery) ||
          email.toLowerCase().includes(lowercaseQuery)
        );
      });
    }
    
    return filtered.slice(0, 8).map((er) => {
      const firstName = erGetCPValue(er, "system-firstName") || "";
      const lastName = erGetCPValue(er, "system-lastName") || "";
      const displayName = [firstName, lastName].filter(Boolean).join(" ").trim() || er.workEmail || "Employee";
      return {
        id: `employee-record-${er.id}`,
        display: displayName,
      };
    });
  };
  
  const renderEmployeeRecordSuggestion = (
    suggestion: any,
    search: string,
    highlightedDisplay: React.ReactNode,
    index: number,
    focused: boolean
  ) => {
    const recordId = suggestion.id.replace("employee-record-", "");
    const employeeRecord = employeeRecords?.find(er => er.id === recordId);
    
    if (!employeeRecord) return null;
    
    const firstName = erGetCPValue(employeeRecord, "system-firstName") || "";
    const lastName = erGetCPValue(employeeRecord, "system-lastName") || "";
    const displayName = [firstName, lastName].filter(Boolean).join(" ").trim() || employeeRecord.workEmail || "Employee";
    const fallbackAvatar = getDefaultUserAvatar(displayName || employeeRecord.workEmail || "");
    
    return (
      <div
        key={suggestion.id}
        className="mention-suggestion-option"
        style={{
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          fontSize: "13px",
          color: "#333",
          backgroundColor: focused ? "#f8f8f8" : "transparent",
          transition: "background-color 0.1s ease",
        }}>
        <ValidatedImage
          imageUrl={employeeRecord.avatarURL || ""}
          fallbackUrl={fallbackAvatar}
          alt={displayName}
          className="mention-suggestion-avatar"
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: "500" }}>{highlightedDisplay}</div>
          {employeeRecord.workEmail && (
            <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
              {employeeRecord.workEmail}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleCustomPropertiesChange = (value: string) => {
    setCustomProperties(value);
    setHasChanges(true);
  };

  const handleNameEdit = () => {
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    setIsEditingName(false);
    setHasChanges(true);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const handleActiveStatusChange = (checked: boolean) => {
    setActive(checked);
    setHasChanges(true);
  };

  const handleAllowInReportingChange = (checked: boolean) => {
    setAllowInReporting(checked);
    setHasChanges(true);
  };

  const handleCustomPropertyChange = (label: string, data: any) => {
    const updatedValues = new Map(customPropertyValues);
    updatedValues.set(label, data);
    setCustomPropertyValues(updatedValues);
    setHasChanges(true);
  };

  // Drag and drop handlers (commented out)
  /*
  const handleDragStart = (start: any) => {
    console.log('Drag started:', start);
    setDraggedProperty(start.draggableId);
  };

  const handleDragUpdate = (update: any) => {
    const { destination } = update;
    if (destination) {
      setDropTarget({
        column: destination.droppableId,
        index: destination.index
      });
    } else {
      setDropTarget(null);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    console.log('Drag ended:', result);

    // Clear drag state
    setDraggedProperty(null);
    setDropTarget(null);

    // If dropped outside a droppable area
    if (!destination) {
      return;
    }

    // If dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const sourceColumn = source.droppableId === 'left-column' ? leftColumnProperties : rightColumnProperties;
    const destColumn = destination.droppableId === 'left-column' ? leftColumnProperties : rightColumnProperties;
    
    // Find the dragged property
    const draggedProperty = sourceColumn.find(prop => prop.label === draggableId);
    if (!draggedProperty) return;

    // Create new arrays for both columns
    const newSourceColumn = Array.from(sourceColumn);
    const newDestColumn = Array.from(destColumn);

    // Remove from source
    newSourceColumn.splice(source.index, 1);

    // Add to destination - insert above the target property
    if (source.droppableId === destination.droppableId) {
      // Moving within the same column
      newSourceColumn.splice(destination.index, 0, draggedProperty);
    } else {
      // Moving between columns
      newDestColumn.splice(destination.index, 0, draggedProperty);
    }

    // Update state
    if (source.droppableId === 'left-column') {
      setLeftColumnProperties(newSourceColumn);
      if (destination.droppableId === 'right-column') {
        setRightColumnProperties(newDestColumn);
      }
    } else {
      setRightColumnProperties(newSourceColumn);
      if (destination.droppableId === 'left-column') {
        setLeftColumnProperties(newDestColumn);
      }
    }

    setHasChanges(true);
  };

  const getPropertyClasses = (property: CustomProperty, columnId: string, index: number, isDragging: boolean) => {
    const classes = ['app-vibe-process-editor-property'];
    
    if (isDragging) {
      classes.push('dragging');
    }
    
    if (dropTarget && dropTarget.column === columnId && dropTarget.index === index) {
      classes.push('drop-target');
    }
    
    return classes.join(' ');
  };
  */

  const handleMenuToggle = (propertyLabel: string) => {
    setOpenMenu(openMenu === propertyLabel ? null : propertyLabel);
  };

  const handleMoveToColumn = (propertyLabel: string, fromColumn: string) => {
    const targetColumn = fromColumn === 'left-column' ? 'right-column' : 'left-column';
    
    if (fromColumn === 'left-column') {
      // Move from left to right
      const property = leftColumnProperties.find(p => p.label === propertyLabel);
      if (property) {
        const newLeftColumn = leftColumnProperties.filter(p => p.label !== propertyLabel);
        const newRightColumn = [...rightColumnProperties, property];
        setLeftColumnProperties(newLeftColumn);
        setRightColumnProperties(newRightColumn);
      }
    } else {
      // Move from right to left
      const property = rightColumnProperties.find(p => p.label === propertyLabel);
      if (property) {
        const newRightColumn = rightColumnProperties.filter(p => p.label !== propertyLabel);
        const newLeftColumn = [...leftColumnProperties, property];
        setRightColumnProperties(newRightColumn);
        setLeftColumnProperties(newLeftColumn);
      }
    }
    
    setOpenMenu(null);
    setHasChanges(true);
  };

  const handleMoveUp = (propertyLabel: string, columnId: string) => {
    if (columnId === 'left-column') {
      const index = leftColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index > 0) {
        const newColumn = [...leftColumnProperties];
        [newColumn[index - 1], newColumn[index]] = [newColumn[index], newColumn[index - 1]];
        setLeftColumnProperties(newColumn);
      }
    } else {
      const index = rightColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index > 0) {
        const newColumn = [...rightColumnProperties];
        [newColumn[index - 1], newColumn[index]] = [newColumn[index], newColumn[index - 1]];
        setRightColumnProperties(newColumn);
      }
    }
    
    setOpenMenu(null);
    setHasChanges(true);
  };

  const handleMoveDown = (propertyLabel: string, columnId: string) => {
    if (columnId === 'left-column') {
      const index = leftColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index < leftColumnProperties.length - 1) {
        const newColumn = [...leftColumnProperties];
        [newColumn[index], newColumn[index + 1]] = [newColumn[index + 1], newColumn[index]];
        setLeftColumnProperties(newColumn);
      }
    } else {
      const index = rightColumnProperties.findIndex(p => p.label === propertyLabel);
      if (index < rightColumnProperties.length - 1) {
        const newColumn = [...rightColumnProperties];
        [newColumn[index], newColumn[index + 1]] = [newColumn[index + 1], newColumn[index]];
        setRightColumnProperties(newColumn);
      }
    }
    
    setOpenMenu(null);
    setHasChanges(true);
  };

  const handleToggleRequired = (propertyLabel: string) => {
    const isRequired = requiredProperties.includes(propertyLabel);
    let newRequiredProperties: string[];
    
    if (isRequired) {
      // Remove from required list (make optional)
      newRequiredProperties = requiredProperties.filter(label => label !== propertyLabel);
    } else {
      // Add to required list (make required)
      newRequiredProperties = [...requiredProperties, propertyLabel];
    }
    
    setRequiredProperties(newRequiredProperties);
    setOpenMenu(null);
    setHasChanges(true);
  };
  
  const isPropertyRequired = (propertyLabel: string): boolean => {
    return requiredProperties.includes(propertyLabel);
  };

  const handleAddProperty = async (property: CustomProperty) => {
    if (!vibeProcess) {
      toast("Vibe process must be saved first.", toastFail);
      return;
    }

    try {
      // Reload workspace custom properties to get the latest (including any newly created ones)
      const updatedWorkspaceProps = await apiGetCustomPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");
      if (updatedWorkspaceProps) {
        setWorkspaceCustomProperties(updatedWorkspaceProps);
      }

      // Add to the column with fewer elements
      const targetColumn = leftColumnProperties.length <= rightColumnProperties.length ? 'left' : 'right';

      // Update vibe process properties
      const updatedVibeProcessProps = [...vibeProcessProperties, property];
      setVibeProcessProperties(updatedVibeProcessProps);

      // Add to layout (smallest column)
      if (targetColumn === 'left') {
        setLeftColumnProperties([...leftColumnProperties, property]);
      } else {
        setRightColumnProperties([...rightColumnProperties, property]);
      }

      // Update customProperties JSON
      try {
        const currentProps = JSON.parse(customProperties || "[]");
        const propertyEntry = {
          label: property.label,
          type: property.type,
          display: property.display,
          description: property.description || "",
        };
        const updatedProps = [...currentProps, propertyEntry];
        setCustomProperties(JSON.stringify(updatedProps));
      } catch (error) {
        console.error("Error updating customProperties JSON:", error);
        toast("Failed to update property list.", toastFail);
        return;
      }

      // Update required properties based on the property's required value
      if (property.required) {
        const updatedRequired = [...requiredProperties, property.label];
        setRequiredProperties(updatedRequired);
      }

      // Save all changes immediately
      await saveVibeProcessWithProperty(property);
      
      toast("Property added successfully!", toastSuccess);
      toast("Process saved", toastSuccess);
    } catch (error) {
      console.error("Error adding property:", error);
      toast("Failed to add property.", toastFail);
    }
  };

  const saveVibeProcessWithProperty = async (newProperty: CustomProperty) => {
    if (!vibeProcess || !storeUser?.currentWorkspaceId || !storeUser?.id) {
      return;
    }

    try {
      setSaving(true);

      // Get current layout
      const currentLayout = getCurrentLayout();
      const layoutJson = JSON.stringify(currentLayout);
      
      // Get updated required properties
      const requiredProps = newProperty.required 
        ? [...requiredProperties, newProperty.label]
        : requiredProperties;
      const requiredPropertiesJson = JSON.stringify(requiredProps);
      
      // Get updated custom properties JSON
      const currentProps = JSON.parse(customProperties || "[]");
      const propertyEntry = {
        label: newProperty.label,
        type: newProperty.type,
        display: newProperty.display,
        description: newProperty.description || "",
      };
      const updatedProps = [...currentProps, propertyEntry];
      const updatedCustomPropsJson = JSON.stringify(updatedProps);

      const updatePayload = {
        name,
        description,
        creationPrompt,
        workflowPrompt,
        customProperties: updatedCustomPropsJson,
        requiredProperties: requiredPropertiesJson,
        layout: layoutJson,
        active,
        allowInReporting
      };
      
      const updatedProcess = await apiUpdateVibeProcess(vibeProcess.id, updatePayload);
      
      if (updatedProcess) {
        setVibeProcess(updatedProcess);
        setCustomProperties(updatedCustomPropsJson);
        setRequiredProperties(requiredProps);
        setHasChanges(false);
      } else {
        toast("Failed to save property changes.", toastFail);
      }
    } catch (error) {
      console.error("Error saving vibe process:", error);
      toast("Error saving property changes.", toastFail);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = (propertyLabel: string) => {
    // Find the property to get its ID, display name, and isVibe status
    // Check both workspace properties and vibe process properties
    const property = workspaceCustomProperties.find(cp => cp.label === propertyLabel) ||
                     vibeProcessProperties.find(cp => cp.label === propertyLabel);
    if (property) {
      setPropertyToDelete({
        label: propertyLabel,
        display: property.display || propertyLabel,
        id: property.id,
        isVibe: property.isVibe
      });
      setShowDeleteConfirmModal(true);
      setOpenMenu(null);
    }
  };

  const confirmDeleteProperty = async () => {
    if (!propertyToDelete || !vibeProcess) {
      setShowDeleteConfirmModal(false);
      setPropertyToDelete(null);
      return;
    }

    try {
      // Delete from custom property database if it's a vibe property
      if (propertyToDelete.isVibe && propertyToDelete.id) {
        const deleteResult = await apiDeleteCustomProperty(propertyToDelete.id);
        if (!deleteResult) {
          toast("Failed to remove custom property from database.", toastFail);
          setShowDeleteConfirmModal(false);
          setPropertyToDelete(null);
          return;
        }
      }

      // Remove from left column if present
      const newLeftColumn = leftColumnProperties.filter(p => p.label !== propertyToDelete.label);
      setLeftColumnProperties(newLeftColumn);

      // Remove from right column if present
      const newRightColumn = rightColumnProperties.filter(p => p.label !== propertyToDelete.label);
      setRightColumnProperties(newRightColumn);

      // Remove from required properties if present
      const newRequiredProperties = requiredProperties.filter(label => label !== propertyToDelete.label);
      setRequiredProperties(newRequiredProperties);

      // Remove from customProperties JSON string
      let updatedCustomPropsJson: string;
      try {
        const customProps = JSON.parse(customProperties || "[]");
        const updatedCustomProps = customProps.filter((prop: any) => prop.label !== propertyToDelete.label);
        updatedCustomPropsJson = JSON.stringify(updatedCustomProps);
        setCustomProperties(updatedCustomPropsJson);
      } catch (error) {
        console.error("Error parsing custom properties:", error);
        updatedCustomPropsJson = customProperties;
      }

      // Save the vibe process with all changes
      const currentLayout = getCurrentLayout();
      const layoutJson = JSON.stringify(currentLayout);
      const requiredPropertiesJson = JSON.stringify(newRequiredProperties);
      
      const updatePayload = {
        name,
        description,
        creationPrompt,
        workflowPrompt,
        customProperties: updatedCustomPropsJson,
        requiredProperties: requiredPropertiesJson,
        layout: layoutJson,
        active
      };

      const updatedProcess = await apiUpdateVibeProcess(vibeProcess.id, updatePayload);

      if (updatedProcess) {
        setVibeProcess(updatedProcess);
        toast("Property removed and changes saved successfully!", toastSuccess);
        setHasChanges(false);
      } else {
        toast("Failed to save changes after removal.", toastFail);
      }

      setShowDeleteConfirmModal(false);
      setPropertyToDelete(null);
    } catch (error) {
      console.error("Error removing property:", error);
      toast("Error removing property.", toastFail);
      setShowDeleteConfirmModal(false);
      setPropertyToDelete(null);
    }
  };

  const handleDeleteVibeProcess = () => {
    const confirmed = window.confirm(`Delete Vibe Process: ${name}\n\nAre you sure you want to delete this vibe process? This action cannot be undone.`);
    if (confirmed) {
      alert("Delete process is not implemented yet.");
    }
  };

  const handleUpdateWorkflow = async () => {
    
    if (!vibeProcess || !workflowPrompt) {
      return;
    }

    const payload: CreateVibeProcessWorkflowPayload = {
      workflowPrompt: workflowPrompt,
    };

    toast("Generating workflow", toastSuccess);
    
    const workflowJSON = await apiCreateVibeProcessWorkflow(payload);

    if (!workflowJSON) {
      toast("Failed to update workflow.", toastFail);
      return;
    }

    const updatedVibeProcess = await apiUpdateVibeProcess(vibeProcess.id, {
      workflowDiagram: JSON.stringify(workflowJSON) || "",
    });

    setVibeProcess(updatedVibeProcess);
    setShowVisualizeModal(true);
  };

  const handleOpenSourceModal = () => {
    if (vibeProcess?.workflowDiagram) {
      try {
        const parsed = JSON.parse(vibeProcess.workflowDiagram);
        setWorkflowSource(JSON.stringify(parsed, null, 2));
        setSourceError(null);
      } catch (error) {
        setWorkflowSource(vibeProcess.workflowDiagram);
        setSourceError("Invalid JSON format");
      }
    } else {
      setWorkflowSource("{}");
      setSourceError(null);
    }
    setIsEditingSource(false);
    setShowSourceModal(true);
  };

  const handleEditSource = () => {
    setIsEditingSource(true);
    setSourceError(null);
  };

  const handleCancelEdit = () => {
    if (vibeProcess?.workflowDiagram) {
      try {
        const parsed = JSON.parse(vibeProcess.workflowDiagram);
        setWorkflowSource(JSON.stringify(parsed, null, 2));
      } catch (error) {
        setWorkflowSource(vibeProcess.workflowDiagram);
      }
    } else {
      setWorkflowSource("{}");
    }
    setIsEditingSource(false);
    setSourceError(null);
  };

  const handleSaveSource = async () => {
    try {
      // Validate JSON
      const parsed = JSON.parse(workflowSource);
      setSourceError(null);

      if (!vibeProcess) {
        toast("No vibe process to update.", toastFail);
        return;
      }

      // Update the vibe process with the new workflow diagram
      const updatePayload = {
        name: vibeProcess.name,
        description: vibeProcess.description,
        creationPrompt: vibeProcess.creationPrompt,
        workflowPrompt: vibeProcess.workflowPrompt,
        customProperties: vibeProcess.customProperties,
        workflowDiagram: JSON.stringify(parsed),
        active: vibeProcess.active,
        allowInReporting: vibeProcess.allowInReporting ?? true
      };

      const updatedProcess = await apiUpdateVibeProcess(vibeProcess.id, updatePayload);
      
      if (updatedProcess) {
        setVibeProcess(updatedProcess);
        setIsEditingSource(false);
        toast("Workflow source updated successfully!", toastSuccess);
      } else {
        toast("Failed to update workflow source.", toastFail);
      }
    } catch (error) {
      setSourceError("Invalid JSON format. Please check your syntax.");
      console.error("Error parsing JSON:", error);
    }
  };

  const handleSourceChange = (value: string) => {
    setWorkflowSource(value);
    setSourceError(null);
  };


  console.log("[AppVibeProcessEditor] vibeProccessId:", vibeProcessId);
  console.log("[AppVibeProcessEditor] vpid:", vpid);
  console.log("[AppVibeProcessEditor] vibeProcess:", vibeProcess);
  console.log("[AppVibeProcessEditor] vibeProcessProperties:", vibeProcessProperties);
  console.log("[AppVibeProcessEditor] rightColumnProperties:", rightColumnProperties);
  console.log("[AppVibeProcessEditor] leftColumnProperties:", leftColumnProperties);

  if (loading) {
    return (
      <div className="app-vibe-process-editor">
        <div className="app-vibe-process-editor-loading">
          <div className="app-vibe-process-editor-loading-spinner"></div>
          <p>Loading vibe process...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-vibe-process-editor">
      {/* Header */}
      <div className="app-vibe-process-editor-header">
        <div className="app-vibe-process-editor-header-left">
          <div className="app-vibe-process-editor-title">
            <div className="app-vibe-process-editor-name-container">
              {isEditingName ? (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onBlur={handleNameSave}
                  onKeyDown={handleNameKeyDown}
                  className="app-vibe-process-editor-name-input"
                  maxLength={40}
                  autoFocus
                />
              ) : (
                <h1 
                  className="app-vibe-process-editor-name-display"
                  onClick={handleNameEdit}
                >
                  {name || (vibeProcess ? "Edit Vibe Process" : "Create Vibe Process")}
                </h1>
              )}
            </div>
          </div>
        </div>
        
        <div className="app-vibe-process-editor-header-actions">
          <div className="app-vibe-process-editor-status">
            <div className="app-vibe-process-editor-status-label">Active</div>
            <MySwitch
              checked={active}
              onChange={handleActiveStatusChange}
            />
          </div>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Save size={16} />}
            label={saving ? "Saving..." : "Save"}
            onClick={handleSave}
            disabled={saving || !hasChanges}
          />
          {vibeProcess && (
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Trash2 size={16} />}
              label="Delete"
              onClick={handleDeleteVibeProcess}
              disabled={saving}
            />
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="app-vibe-process-editor-tabs">
        <div 
          className={`app-vibe-process-editor-tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <FileText size={16} />
          <span>Overview</span>
        </div>
        <div 
          className={`app-vibe-process-editor-tab ${activeTab === "workflow" ? "active" : ""}`}
          onClick={() => setActiveTab("workflow")}
        >
          <Code size={16} />
          <span>Workflow</span>
        </div>
        <div 
          className={`app-vibe-process-editor-tab ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          <Settings size={16} />
          <span>Settings</span>
        </div>
      </div>

      {/* Content */}
      <div className="app-vibe-process-editor-content">
        {activeTab === "overview" && (
          <div className="app-vibe-process-editor-overview">
            <div className="app-vibe-process-editor-section">
              {/* <h3>Basic Information</h3> */}
              <div className="app-vibe-process-property">
                <div className="app-vibe-process-property-label">
                  How to Trigger Workflow
                </div>
                <div className="app-vibe-process-property-value">
                  <textarea
                    value={description}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    placeholder="Describe how to trigger this workflow"
                    rows={4}
                  />
                </div>
              </div>
              <div className="app-vibe-process-property app-vibe-process-property-row">
                <span className="app-vibe-process-allow-in-reporting-label">
                  Allow in Reporting
                </span>
                <div className="app-vibe-process-property-value">
                  <MySwitch
                    checked={allowInReporting}
                    onChange={handleAllowInReportingChange}
                  />
                </div>
              </div>
            </div>

            <div className="app-vibe-process-editor-preview-header">
              <h3>Properties</h3>
              <Button
                variant="secondary"
                size="md"
                leftIcon={<Settings size={16} />}
                label="Add"
                onClick={() => setShowAddPropertyModal(true)}
                disabled={!vibeProcess}
              />
            </div>

            {(leftColumnProperties.length > 0 || rightColumnProperties.length > 0) && (
              <div className="app-vibe-process-editor-preview">
                
                {/* DragDropContext 
                  onDragStart={handleDragStart}
                  onDragUpdate={handleDragUpdate}
                  onDragEnd={handleDragEnd}
                > */}
                  <div className="app-vibe-process-editor-two-column-layout">
                    {/* Left Column */}
                    <div className="app-vibe-process-editor-column">
                      <div className="app-vibe-process-editor-column-content">
                        {leftColumnProperties.map((customProperty: CustomProperty, index: number) => (
                          <div
                            key={customProperty.label}
                            className="app-vibe-process-editor-property"
                          >
                            <div className="app-vibe-process-editor-property-content">
                              <div className="app-vibe-process-editor-property-left">
                                <div className="app-vibe-process-editor-property-label-container">
                                  <div className="app-vibe-process-editor-property-label">
                                    {customProperty.display}
                                  </div>
                                  {customProperty.description && (
                                    <MyTooltip title={customProperty.description}>
                                      <Info size={16} className="app-vibe-process-editor-info-icon" />
                                    </MyTooltip>
                                  )}
                                </div>
                                {!isPropertyRequired(customProperty.label) && (
                                  <div className="app-vibe-process-editor-property-optional">
                                    OPTIONAL
                                  </div>
                                )}
                              </div>
                              <div className="app-vibe-process-editor-property-middle">
                                <AppCustomProperty
                                  customProperty={customProperty}
                                  data={customPropertyValues.get(customProperty.label) || undefined}
                                  validate={false}
                                  onChange={handleCustomPropertyChange}
                                />
                              </div>
                              <div className="app-vibe-process-editor-property-right">
                                <div className="app-vibe-process-editor-property-menu">
                                  <button
                                    className="app-vibe-process-editor-menu-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuToggle(customProperty.label);
                                    }}
                                  >
                                    <MoreHorizontal size={16} />
                                  </button>
                                  {openMenu === customProperty.label && (
                                    <div className="app-vibe-process-editor-menu-dropdown">
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleMoveToColumn(customProperty.label, 'left-column')}
                                      >
                                        <ArrowRight size={14} />
                                        Move to Right Column
                                      </button>
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleMoveUp(customProperty.label, 'left-column')}
                                        disabled={index === 0}
                                      >
                                        <ArrowUp size={14} />
                                        Move Up
                                      </button>
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleMoveDown(customProperty.label, 'left-column')}
                                        disabled={index === leftColumnProperties.length - 1}
                                      >
                                        <ArrowDown size={14} />
                                        Move Down
                                      </button>
                                      <div className="app-vibe-process-editor-menu-divider"></div>
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleToggleRequired(customProperty.label)}
                                      >
                                        <Shield size={14} />
                                        {isPropertyRequired(customProperty.label) ? "Make Optional" : "Make Required"}
                                      </button>
                                      <button
                                        className="app-vibe-process-editor-menu-item app-vibe-process-editor-menu-item-danger"
                                        onClick={() => handleDeleteProperty(customProperty.label)}
                                      >
                                        <X size={14} />
                                        Remove
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="app-vibe-process-editor-column">
                      <div className="app-vibe-process-editor-column-content">
                        {rightColumnProperties.map((customProperty: CustomProperty, index: number) => (
                          <div
                            key={customProperty.label}
                            className="app-vibe-process-editor-property"
                          >
                            <div className="app-vibe-process-editor-property-content">
                              <div className="app-vibe-process-editor-property-left">
                                <div className="app-vibe-process-editor-property-label-container">
                                  <div className="app-vibe-process-editor-property-label">
                                    {customProperty.display}
                                  </div>
                                  {customProperty.description && (
                                    <MyTooltip title={customProperty.description}>
                                      <Info size={16} className="app-vibe-process-editor-info-icon" />
                                    </MyTooltip>
                                  )}
                                </div>
                                {!isPropertyRequired(customProperty.label) && (
                                  <div className="app-vibe-process-editor-property-optional">
                                    OPTIONAL
                                  </div>
                                )}
                              </div>
                              <div className="app-vibe-process-editor-property-middle">
                                <AppCustomProperty
                                  customProperty={customProperty}
                                  data={customPropertyValues.get(customProperty.label) || undefined}
                                  validate={false}
                                  onChange={handleCustomPropertyChange}
                                />
                              </div>
                              <div className="app-vibe-process-editor-property-right">
                                <div className="app-vibe-process-editor-property-menu">
                                  <button
                                    className="app-vibe-process-editor-menu-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuToggle(customProperty.label);
                                    }}
                                  >
                                    <MoreHorizontal size={16} />
                                  </button>
                                  {openMenu === customProperty.label && (
                                    <div className="app-vibe-process-editor-menu-dropdown">
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleMoveToColumn(customProperty.label, 'right-column')}
                                      >
                                        <ArrowLeft size={14} />
                                        Move to Left Column
                                      </button>
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleMoveUp(customProperty.label, 'right-column')}
                                        disabled={index === 0}
                                      >
                                        <ArrowUp size={14} />
                                        Move Up
                                      </button>
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleMoveDown(customProperty.label, 'right-column')}
                                        disabled={index === rightColumnProperties.length - 1}
                                      >
                                        <ArrowDown size={14} />
                                        Move Down
                                      </button>
                                      <div className="app-vibe-process-editor-menu-divider"></div>
                                      <button
                                        className="app-vibe-process-editor-menu-item"
                                        onClick={() => handleToggleRequired(customProperty.label)}
                                      >
                                        <Shield size={14} />
                                        {isPropertyRequired(customProperty.label) ? "Make Optional" : "Make Required"}
                                      </button>
                                      <button
                                        className="app-vibe-process-editor-menu-item app-vibe-process-editor-menu-item-danger"
                                        onClick={() => handleDeleteProperty(customProperty.label)}
                                      >
                                        <X size={14} />
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                {/* </DragDropContext> */}
              </div>
            )}
          </div>
        )}

        {activeTab === "workflow" && (
          <div className="app-vibe-process-editor-workflow">
            <div className="app-vibe-process-editor-section">
              {/* <h3>Workflow Instructions</h3> */}
              <div className="app-vibe-process-property">
                <div className="app-vibe-process-property-label">
                  Workflow Prompt
                </div>
                <div className="app-vibe-process-property-value">
                  <MentionsInputAny
                    className="mentions app-vibe-process-editor-textarea-mentions"
                    value={workflowPrompt}
                    allowSuggestionsAboveCursor={true}
                    forceSuggestionsAboveCursor={true}
                    suggestionsPortalHost={
                      typeof document !== "undefined" ? document.body : undefined
                    }
                    onChange={(
                      e: any,
                      newValue: string,
                      newPlainTextValue: string
                    ) => {
                      handleWorkflowPromptChange(newValue, newPlainTextValue);
                    }}
                    placeholder="Enter detailed instructions for how this workflow should operate, including specific steps, decision points, or automation rules..."
                    allowSpaceInQuery={false}
                    inputRef={(el: any) => {
                      workflowPromptRef.current = el as HTMLTextAreaElement | null;
                    }}
                    style={{
                      control: {
                        fontSize: "inherit",
                        fontWeight: "normal",
                      },
                      highlighter: {
                        overflow: "hidden",
                      },
                      input: {
                        margin: 0,
                        outline: 0,
                        border: 0,
                        padding: "12px 16px",
                      },
                      "&multiLine": {
                        control: {
                          fontFamily: "inherit",
                          minHeight: 200,
                        },
                        highlighter: {
                          padding: "12px 16px",
                          border: "none",
                        },
                        input: {
                          padding: "12px 16px",
                          border: "none",
                          outline: 0,
                        },
                      },
                      mention: {
                        backgroundColor: "#2563eb",
                        color: "#ffffff",
                        border: "1px solid #1d4ed8",
                        borderRadius: "16px",
                        padding: "4px 8px",
                        fontWeight: 600,
                        fontSize: "13px",
                        display: "inline-flex",
                        alignItems: "center",
                        margin: "0 2px",
                        lineHeight: "1.2",
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.2s ease",
                        textDecoration: "none",
                      },
                      suggestions: {
                        list: {
                          backgroundColor: "white",
                          border: "1px solid #e0e0e0",
                          fontSize: 14,
                          borderRadius: 8,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          maxHeight: 200,
                          overflowY: "auto",
                          zIndex: 1001,
                          position: "absolute",
                          bottom: "100%",
                          top: "auto",
                          left: 0,
                          right: 0,
                          marginBottom: 8,
                          marginTop: 0,
                          width: "100%",
                          // minWidth: 250,
                        },
                        item: {
                          display: "flex",
                          alignItems: "center",
                          padding: "8px",
                          cursor: "pointer",
                          color: "var(--text-color-primary, #333)",
                          gap: "8px",
                          borderBottom: "none",
                          "&focused": {
                            backgroundColor: "#f0f0f0",
                          },
                        },
                      },
                    }}>
                    <MentionAny
                      trigger="@"
                      markup="@[__display__](__id__)"
                      appendSpaceOnAdd={true}
                      displayTransform={(id: string, display: string) =>
                        `@${display}`
                      }
                      data={(query: string, callback: (results: Array<{ id: string; display: string }>) => void) => {
                        // Ensure we always call the callback, even with empty results
                        const results = getEmployeeRecordMentions(query);
                        console.log('[Mentions] Query:', query, 'Results:', results.length, 'Employee records available:', employeeRecords?.length || 0);
                        // react-mentions expects the callback to be called synchronously
                        if (typeof callback === 'function') {
                          callback(results);
                        }
                      }}
                      renderSuggestion={renderEmployeeRecordSuggestion}
                    />
                  </MentionsInputAny>
                </div>
              </div>
              <div className="app-vibe-process-editor-section">
                <div className="app-vibe-process-editor-button-group">
                  <Button
                    variant="primary"
                    size="md"
                    leftIcon={<Play size={16} />}
                    label="Update"
                    onClick={() => {
                      if (!workflowPrompt.trim()) {
                        alert("Please enter workflow instructions first.");
                        return;
                      }
                      handleUpdateWorkflow();
                    }}
                    disabled={!workflowPrompt.trim()}
                  />
                  {vibeProcess?.workflowDiagram && (
                    <>
                      <Button
                        variant="secondary"
                        size="md"
                        leftIcon={<Code size={16} />}
                        label="Visualize"
                        onClick={() => setShowVisualizeModal(true)}
                      />
                      <Button
                        variant="secondary"
                        size="md"
                        leftIcon={<FileText size={16} />}
                        label="Source"
                        onClick={handleOpenSourceModal}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            
          </div>
        )}

        {activeTab === "settings" && (
          <div className="app-vibe-process-editor-settings">
            <div className="app-vibe-process-editor-section">
              <h3>Custom Properties</h3>
              <div className="app-custom-property">
                <div className="app-custom-property-label">
                  JSON Configuration
                </div>
                <div className="app-custom-property-value">
                  <textarea
                    value={customProperties}
                    onChange={(e) => handleCustomPropertiesChange(e.target.value)}
                    placeholder='{"key": "value"}'
                    rows={8}
                  />
                </div>
              </div>
            </div>

            <div className="app-vibe-process-editor-section">
              <h3>Creation Prompt</h3>
              <div className="app-custom-property">
                <div className="app-custom-property-label">
                  Original Request
                </div>
                <div className="app-custom-property-value">
                  <textarea
                    value={creationPrompt}
                    readOnly
                    placeholder="No creation prompt available"
                    rows={6}
                    className="app-vibe-process-editor-readonly-textarea"
                  />
                </div>
              </div>
            </div>

            <div className="app-vibe-process-editor-section">
              <h3>Workflow Prompt</h3>
              <div className="app-custom-property">
                {/* <div className="app-custom-property-label">
                  Workflow Instructions
                </div> */}
                <div className="app-custom-property-value">
                  <textarea
                    value={workflowPrompt}
                    onChange={(e) => handleWorkflowPromptChange(e.target.value)}
                    placeholder="Enter detailed instructions for how this workflow should operate..."
                    rows={6}
                  />
                </div>
              </div>
            </div>

          </div>
        )}
      </div>

      {/* Visualize Modal */}
      {showVisualizeModal && (
        <div className="app-vibe-process-editor-modal-overlay">
          <div className="app-vibe-process-editor-modal">
            <div className="app-vibe-process-editor-modal-header">
              <h2>Workflow Visualization</h2>
              <button
                className="app-vibe-process-editor-modal-close"
                onClick={() => setShowVisualizeModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="app-vibe-process-editor-modal-content">
              <WorkflowDiagram workflowData={vibeProcess?.workflowDiagram || ''} />
            </div>
          </div>
        </div>
      )}

      {/* View Source Modal */}
      {showSourceModal && (
        <div className="app-vibe-process-editor-modal-overlay">
          <div className="app-vibe-process-editor-modal">
            <div className="app-vibe-process-editor-modal-header">
              <h2>Workflow Source (JSON)</h2>
              <div className="app-vibe-process-editor-modal-actions">
                {!isEditingSource ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Edit3 size={16} />}
                    label="Edit"
                    onClick={handleEditSource}
                  />
                ) : (
                  <div className="app-vibe-process-editor-modal-edit-actions">
                    <Button
                      variant="secondary"
                      size="sm"
                      label="Cancel"
                      onClick={handleCancelEdit}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Save size={16} />}
                      label="Save"
                      onClick={handleSaveSource}
                    />
                  </div>
                )}
                <button
                  className="app-vibe-process-editor-modal-close"
                  onClick={() => setShowSourceModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="app-vibe-process-editor-modal-content">
              {sourceError && (
                <div className="app-vibe-process-editor-source-error">
                  <AlertCircle size={16} />
                  <span>{sourceError}</span>
                </div>
              )}
              {isEditingSource ? (
                <textarea
                  className="app-vibe-process-editor-json-editor"
                  value={workflowSource}
                  onChange={(e) => handleSourceChange(e.target.value)}
                  placeholder="Enter workflow JSON..."
                  spellCheck={false}
                />
              ) : (
                <pre className="app-vibe-process-editor-json-display">
                  {workflowSource}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ModalConfirm
        title="Remove Property"
        text={propertyToDelete ? 
          `Are you sure you want to remove "${propertyToDelete.display || propertyToDelete.label}" from this vibe process? ${propertyToDelete.isVibe ? "This will also delete the custom property from the database. " : ""}All changes will be saved automatically. This action cannot be undone.` : 
          ""
        }
        isOpen={showDeleteConfirmModal}
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setPropertyToDelete(null);
        }}
        onContinue={confirmDeleteProperty}
      />

      {/* Add Property Modal */}
      <AddPropertyModal
        isOpen={showAddPropertyModal}
        onClose={() => setShowAddPropertyModal(false)}
        onAddProperty={handleAddProperty}
        existingPropertyLabels={vibeProcessProperties.map(p => p.label)}
        workspaceId={storeUser?.currentWorkspaceId || ""}
      />
    </div>
  );
};
